import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Code2 } from "lucide-react";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "About", path: "/about" },
  { name: "Team", path: "/team" },
  {
    name: "Products",
    path: "/products",
    children: [
      { name: "Vidyora", path: "/vidyora" },
    ],
  },
  { name: "Blog", path: "/blog" },
  { name: "Careers", path: "/careers" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-slate-950/80 backdrop-blur-md border-b border-white/5 shadow-lg"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center shadow-[0_0_20px_rgba(99,102,241,0.3)] group-hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] transition-all">
              <Code2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-['Outfit'] font-bold text-lg text-slate-50 tracking-tight">
              Scriptovative
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.name} className="relative"
                  onMouseEnter={() => setDropdownOpen(true)}
                  onMouseLeave={() => setDropdownOpen(false)}
                >
                  <button
                    data-testid="nav-products-dropdown"
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === "/products" || location.pathname === "/vidyora"
                        ? "text-indigo-400"
                        : "text-slate-400 hover:text-slate-50"
                    }`}
                  >
                    {link.name}
                    <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                  </button>
                  {dropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-slate-900/95 backdrop-blur-md border border-slate-800 rounded-xl shadow-xl overflow-hidden">
                      <Link
                        to="/products"
                        data-testid="nav-products-link"
                        className="block px-4 py-3 text-sm text-slate-300 hover:text-white hover:bg-slate-800/50 transition-colors"
                      >
                        All Products
                      </Link>
                      {link.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.path}
                          data-testid={`nav-${child.name.toLowerCase()}-link`}
                          className="block px-4 py-3 text-sm text-slate-300 hover:text-white hover:bg-slate-800/50 transition-colors"
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}-link`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === link.path
                      ? "text-indigo-400"
                      : "text-slate-400 hover:text-slate-50"
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
          </div>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="hidden lg:inline-flex bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)]"
            >
              Get in Touch
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 text-slate-400 hover:text-white transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-slate-950/95 backdrop-blur-md border-t border-white/5">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.name}>
                  <Link
                    to="/products"
                    data-testid="mobile-products-link"
                    className="block px-4 py-3 text-base text-slate-300 hover:text-white rounded-lg hover:bg-slate-800/50 transition-colors"
                  >
                    Products
                  </Link>
                  {link.children.map((child) => (
                    <Link
                      key={child.name}
                      to={child.path}
                      data-testid={`mobile-${child.name.toLowerCase()}-link`}
                      className="block px-8 py-2 text-sm text-slate-400 hover:text-white rounded-lg hover:bg-slate-800/50 transition-colors"
                    >
                      {child.name}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={`mobile-${link.name.toLowerCase()}-link`}
                  className={`block px-4 py-3 text-base rounded-lg transition-colors ${
                    location.pathname === link.path
                      ? "text-indigo-400 bg-indigo-500/10"
                      : "text-slate-300 hover:text-white hover:bg-slate-800/50"
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
            <Link
              to="/contact"
              data-testid="mobile-cta-button"
              className="block mx-4 mt-4 text-center bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3 rounded-full text-sm font-medium transition-all"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
